import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type CouponStripeInfo = {
  valid: boolean;
  times_redeemed: number;
  livemode: boolean;
} | null;

type CouponRecord = {
  id: string;
  stripe_coupon_id: string;
  name: string | null;
  duration: string;
  duration_in_months: number | null;
  percent_off: number | null;
  amount_off: number | null;
  max_redemptions: number | null;
  redeem_by: string | null;
  metadata: Record<string, string>;
  allowed_tiers: string;
  allow_bulk: boolean;
  min_quantity: number;
  min_amount: number | null;
  archived_at: string | null;
  archived_reason: string | null;
  source?: string | null;
  stripe?: CouponStripeInfo;
};

const DEFAULT_FORM = {
  recordId: null as string | null,
  couponId: "",
  name: "",
  duration: "forever",
  durationInMonths: "",
  discountType: "percent" as "percent" | "amount",
  percentOff: "",
  amountOff: "",
  maxRedemptions: "",
  redeemBy: "",
  metadata: "{}",
  allowedTiers: "both",
  allowBulk: true,
  minQuantity: "1",
  minAmount: "",
};

export function CouponManager() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<string | null>(null);
  const [editingStripeId, setEditingStripeId] = useState<string | null>(null);

  const { data: coupons, isLoading } = useQuery({
    queryKey: ["admin-coupons"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-coupons-list");
      if (error || data?.error) {
        throw new Error(error?.message || data?.error || "Unable to load coupons.");
      }
      return (data?.coupons || []) as CouponRecord[];
    },
  });

  const activeCoupons = useMemo(() => (coupons || []).filter((c) => !c.archived_at), [coupons]);
  const archivedCoupons = useMemo(() => (coupons || []).filter((c) => c.archived_at), [coupons]);

  const resetForm = () => setForm(DEFAULT_FORM);

  const handleEdit = (coupon: CouponRecord) => {
    setForm({
      recordId: coupon.id,
      couponId: "",
      name: coupon.name || "",
      duration: coupon.duration,
      durationInMonths: coupon.duration_in_months ? String(coupon.duration_in_months) : "",
      discountType: coupon.percent_off ? "percent" : "amount",
      percentOff: coupon.percent_off ? String(coupon.percent_off) : "",
      amountOff: coupon.amount_off ? String(coupon.amount_off) : "",
      maxRedemptions: coupon.max_redemptions ? String(coupon.max_redemptions) : "",
      redeemBy: coupon.redeem_by ? new Date(coupon.redeem_by).toISOString().slice(0, 16) : "",
      metadata: JSON.stringify(coupon.metadata || {}, null, 2),
      allowedTiers: coupon.allowed_tiers,
      allowBulk: coupon.allow_bulk,
      minQuantity: String(coupon.min_quantity || 1),
      minAmount: coupon.min_amount ? String(coupon.min_amount) : "",
    });
    setEditingSource(coupon.source ?? null);
    setEditingStripeId(coupon.stripe_coupon_id);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const duration = form.duration;
    const durationInMonths = form.durationInMonths ? Number(form.durationInMonths) : null;
    const percentOff = form.discountType === "percent" ? Number(form.percentOff) : null;
    const amountOff = form.discountType === "amount" ? Number(form.amountOff) : null;
    const maxRedemptions = form.maxRedemptions ? Number(form.maxRedemptions) : null;
    const minQuantity = form.minQuantity ? Number(form.minQuantity) : 1;
    const minAmount = form.minAmount ? Number(form.minAmount) : null;

    if (!isRewardful) {
      if (!duration) {
        toast.error("Select a duration.");
        return;
      }
      if (duration === "repeating" && (!durationInMonths || durationInMonths <= 0)) {
        toast.error("Enter duration in months for repeating coupons.");
        return;
      }

      if (form.discountType === "percent" && (!percentOff || percentOff <= 0)) {
        toast.error("Enter a valid percent off.");
        return;
      }
      if (form.discountType === "amount" && (!amountOff || amountOff <= 0)) {
        toast.error("Enter a valid amount off.");
        return;
      }
    }

    if (!Number.isFinite(minQuantity) || minQuantity < 1) {
      toast.error("Minimum quantity must be at least 1.");
      return;
    }

    let metadata = {} as Record<string, string>;
    if (form.metadata.trim()) {
      try {
        const parsed = JSON.parse(form.metadata);
        if (parsed && typeof parsed === "object") {
          metadata = parsed as Record<string, string>;
        }
      } catch {
        toast.error("Metadata must be valid JSON.");
        return;
      }
    }

    const payload = {
      recordId: form.recordId,
      couponId: form.couponId.trim() || undefined,
      name: form.name.trim() || undefined,
      duration,
      durationInMonths: duration === "repeating" ? durationInMonths : undefined,
      percentOff,
      amountOff,
      maxRedemptions,
      redeemBy: form.redeemBy ? new Date(form.redeemBy).toISOString() : undefined,
      metadata,
      allowedTiers: form.allowedTiers,
      allowBulk: form.allowBulk,
      minQuantity,
      minAmount,
    };

    try {
      if (form.recordId) {
        const { data, error } = await supabase.functions.invoke("admin-coupons-update", {
          body: payload,
        });
        if (error || data?.error) throw new Error(error?.message || data?.error || "Unable to update coupon.");
        toast.success(editingSource === "rewardful" ? "Coupon rules updated." : "Coupon updated. The previous coupon was archived.");
      } else {
        const { data, error } = await supabase.functions.invoke("admin-coupons-create", {
          body: payload,
        });
        if (error || data?.error) throw new Error(error?.message || data?.error || "Unable to create coupon.");
        toast.success("Coupon created.");
      }
      resetForm();
      setEditingSource(null);
      setEditingStripeId(null);
      setModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["admin-coupons"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  const isRewardful = editingSource === "rewardful";

  return (
    <Card className="cyber-card w-full mx-auto">
      <CardHeader className="flex flex-col gap-2">
        <CardTitle>Stripe Coupons</CardTitle>
        <CardDescription>Manage coupon codes, eligibility rules, and redemption settings.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">Coupon management</p>
            <p className="text-xs text-muted-foreground">
              Create new coupons or update existing ones. Rewardful coupons are read-only except eligibility rules.
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setEditingSource(null);
              setEditingStripeId(null);
              setModalOpen(true);
            }}
          >
            Create coupon
          </Button>
        </div>

        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{form.recordId ? "Edit coupon" : "Create coupon"}</DialogTitle>
              <DialogDescription>
                {form.recordId
                  ? isRewardful
                    ? "Rewardful coupons are read-only except eligibility rules."
                    : "Editing creates a new Stripe coupon and archives the previous one."
                  : "Configure a new Stripe coupon and its eligibility rules."}
              </DialogDescription>
              {editingStripeId && (
                <p className="text-xs text-muted-foreground">Stripe coupon ID: {editingStripeId}</p>
              )}
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="coupon-id">Coupon code (optional)</Label>
                  <Input
                    id="coupon-id"
                    placeholder="Leave empty for auto-generated"
                    value={form.couponId}
                    disabled={isRewardful}
                    onChange={(e) => setForm((prev) => ({ ...prev, couponId: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coupon-name">Name</Label>
                  <Input
                    id="coupon-name"
                    value={form.name}
                    disabled={isRewardful}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="coupon-duration">Duration</Label>
                  <select
                    id="coupon-duration"
                    value={form.duration}
                    disabled={isRewardful}
                    onChange={(e) => setForm((prev) => ({ ...prev, duration: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md border border-border/50 bg-background/50 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="forever">Forever</option>
                    <option value="once">Once</option>
                    <option value="repeating">Repeating</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration-months">Duration in months</Label>
                  <Input
                    id="duration-months"
                    type="number"
                    min={1}
                    disabled={isRewardful || form.duration !== "repeating"}
                    value={form.durationInMonths}
                    onChange={(e) => setForm((prev) => ({ ...prev, durationInMonths: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-redemptions">Max redemptions</Label>
              <Input
                id="max-redemptions"
                type="number"
                min={1}
                value={form.maxRedemptions}
                onChange={(e) => setForm((prev) => ({ ...prev, maxRedemptions: e.target.value }))}
              />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Discount type</Label>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant={form.discountType === "percent" ? "default" : "outline"}
                      disabled={isRewardful}
                      onClick={() => setForm((prev) => ({ ...prev, discountType: "percent" }))}
                    >
                      Percent off
                    </Button>
                    <Button
                      type="button"
                      variant={form.discountType === "amount" ? "default" : "outline"}
                      disabled={isRewardful}
                      onClick={() => setForm((prev) => ({ ...prev, discountType: "amount" }))}
                    >
                      Amount off (AUD)
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="redeem-by">Redeem by</Label>
                  <Input
                    id="redeem-by"
                    type="datetime-local"
                    disabled={isRewardful}
                    value={form.redeemBy}
                    onChange={(e) => setForm((prev) => ({ ...prev, redeemBy: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="percent-off">Percent off</Label>
                  <Input
                    id="percent-off"
                    type="number"
                    step="0.1"
                    min={0}
                    disabled={isRewardful || form.discountType !== "percent"}
                    value={form.percentOff}
                    onChange={(e) => setForm((prev) => ({ ...prev, percentOff: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount-off">Amount off (AUD)</Label>
                  <Input
                    id="amount-off"
                    type="number"
                    step="0.01"
                    min={0}
                    disabled={isRewardful || form.discountType !== "amount"}
                    value={form.amountOff}
                    onChange={(e) => setForm((prev) => ({ ...prev, amountOff: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="allowed-tiers">Allowed tiers</Label>
                  <select
                    id="allowed-tiers"
                    value={form.allowedTiers}
                    onChange={(e) => setForm((prev) => ({ ...prev, allowedTiers: e.target.value }))}
                    className="w-full px-3 py-2 rounded-md border border-border/50 bg-background/50 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="both">Both</option>
                    <option value="basic">Basic only</option>
                    <option value="essential">Essential only</option>
                    <option value="premium">Premium only</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="min-quantity">Minimum quantity</Label>
                  <Input
                    id="min-quantity"
                    type="number"
                    min={1}
                    value={form.minQuantity}
                    onChange={(e) => setForm((prev) => ({ ...prev, minQuantity: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="min-amount">Minimum subtotal (AUD)</Label>
                  <Input
                    id="min-amount"
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.minAmount}
                    onChange={(e) => setForm((prev) => ({ ...prev, minAmount: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-border/50 bg-muted/20 p-3">
                <div>
                  <Label htmlFor="allow-bulk" className="text-sm">Allow bulk pricing</Label>
                  <p className="text-xs text-muted-foreground">When off, coupon only applies to retail tier pricing.</p>
                </div>
                <Switch
                  id="allow-bulk"
                  checked={form.allowBulk}
                  onCheckedChange={(checked) => setForm((prev) => ({ ...prev, allowBulk: checked }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="metadata">Metadata (JSON)</Label>
                <Textarea
                  id="metadata"
                  value={form.metadata}
                  disabled={isRewardful}
                  onChange={(e) => setForm((prev) => ({ ...prev, metadata: e.target.value }))}
                  className="font-mono text-xs"
                  rows={4}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>{form.recordId ? "Update coupon" : "Create coupon"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Active coupons</h3>
            {isLoading && <span className="text-xs text-muted-foreground">Loading…</span>}
          </div>
          {(activeCoupons || []).length === 0 && (
            <p className="text-sm text-muted-foreground">No active coupons yet.</p>
          )}
          {(activeCoupons || []).map((coupon) => (
            <div key={coupon.id} className="rounded-lg border border-border/50 bg-card/40 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">{coupon.stripe_coupon_id}</p>
                  <p className="text-xs text-muted-foreground">{coupon.name || "No name"}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {coupon.stripe?.valid ? (
                    <Badge variant="outline">Valid</Badge>
                  ) : (
                    <Badge variant="destructive">Invalid</Badge>
                  )}
                  {coupon.source === "rewardful" && <Badge variant="secondary">Rewardful</Badge>}
                  <Badge variant="secondary">{coupon.allowed_tiers}</Badge>
                  {!coupon.allow_bulk && <Badge variant="outline">Retail only</Badge>}
                </div>
              </div>
              <div className="mt-3 grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
                <div>
                  Discount: {coupon.percent_off ? `${coupon.percent_off}%` : `${coupon.amount_off} AUD`}
                </div>
                <div>Duration: {coupon.duration}{coupon.duration_in_months ? ` (${coupon.duration_in_months} mo)` : ""}</div>
                <div>Redeemed: {coupon.stripe?.times_redeemed ?? 0}{coupon.max_redemptions ? ` / ${coupon.max_redemptions}` : ""}</div>
                <div>Min qty: {coupon.min_quantity}</div>
                <div>Min subtotal: {coupon.min_amount ? `${coupon.min_amount} AUD` : "-"}</div>
                <div>Redeem by: {coupon.redeem_by ? new Date(coupon.redeem_by).toLocaleString() : "-"}</div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(coupon)}>
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>

        {archivedCoupons.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Archived coupons</h3>
            {archivedCoupons.map((coupon) => (
              <div key={coupon.id} className="rounded-lg border border-border/50 bg-muted/20 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold">{coupon.stripe_coupon_id}</p>
                    <p className="text-xs text-muted-foreground">{coupon.archived_reason || "Archived"}</p>
                  </div>
                  <Badge variant="outline">Archived</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
